import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

const ENTITLEMENT_ERROR_CODE = 'ORGANIZATION_IS_NOT_ENTITLED';

export type ApiErrorRecovery = (context: {
  error: AxiosError;
  reissueRequest: () => Promise<AxiosResponse>;
}) => Promise<AxiosResponse | null>;

type ApiErrorHandlerDeps = {
  axiosClient: AxiosInstance;
  recover: ApiErrorRecovery;
  notifyEntitlementError: () => void;
};

function notifyIfNotEntitled(
  error: AxiosError<{ code?: string }>,
  notifyEntitlementError: () => void,
): void {
  if (
    error.response?.status === 403 &&
    error.response.data?.code === ENTITLEMENT_ERROR_CODE
  ) {
    notifyEntitlementError();
  }
}

export function createApiErrorHandler({
  axiosClient,
  recover,
  notifyEntitlementError,
}: ApiErrorHandlerDeps): (error: unknown) => Promise<unknown> {
  const retriedRequests = new WeakSet<object>();

  return async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response === undefined) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (!originalRequest || retriedRequests.has(originalRequest)) {
      return Promise.reject(error);
    }
    retriedRequests.add(originalRequest);

    const recovered = await recover({
      error,
      reissueRequest: () => axiosClient(originalRequest),
    });

    if (recovered) {
      return recovered;
    }

    notifyIfNotEntitled(error, notifyEntitlementError);

    return Promise.reject(error);
  };
}
